import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, Button, Space, Tag, App, Tooltip, Alert, Input, theme } from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../store/AuthContext';
import CheckHistoryModal, { HistoryModalData } from '../components/health/CheckHistoryModal';
import QuickCheckModal, { QuickCheckTarget } from '../components/health/QuickCheckModal';
import StepsTable from '../components/health/StepsTable';
import HealthIdentity from '../components/health/HealthIdentity';
import { buildIpColumns, buildProxyColumns } from '../components/health/healthDetailColumns';
import type { HealthDetail, IPHealth, ProxyHealth } from '../components/health/healthDetailTypes';
import { STATUS_COLORS, StatusTimelineBlock } from '../components/health/statusVisuals';

const { Title, Text } = Typography;

// Column sets, identity block and response types live in components/health/;
// this page owns data loading, the proxy filter and the two modals.
const HealthDetailPage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  // ?proxy=host:port lands straight on one endpoint. An operator arrives here
  // holding a proxy address, and a relay can carry dozens of them — without
  // this they have to eyeball the table for the row they already named.
  const [searchParams] = useSearchParams();
  const focusedProxy = searchParams.get('proxy') || '';
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { user } = useAuth();
  const [detail, setDetail] = useState<HealthDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Full history behind a sparkline, and the free-text filter over the proxy
  // table — a relay can carry dozens of endpoints and the table had no way to
  // narrow them down.
  const [historyOf, setHistoryOf] = useState<HistoryModalData | null>(null);
  const [proxyFilter, setProxyFilter] = useState('');
  const [checking, setChecking] = useState(false);
  // Quick-check target: null until a row asks for one, and the modal owns the
  // run itself.
  const [quickCheck, setQuickCheck] = useState<QuickCheckTarget | null>(null);
  const [quickCheckOpen, setQuickCheckOpen] = useState(false);
  const [qcWorkers, setQcWorkers] = useState<{ id: string; name: string; country?: string; city?: string; live?: { online: boolean } }[]>([]);

  useEffect(() => {
    // Admin-only endpoint: non-admins silently keep the "Auto" option.
    apiClient.get('/admin/workers')
      .then(res => { if (res.data?.success) setQcWorkers(res.data.data || []); })
      .catch(() => {});
  }, []);

  const qcWorkerOptions = [
    { value: '', label: 'Auto — external worker preferred' },
    ...qcWorkers.map(w => ({
      value: w.id,
      label: `${w.name}${w.country ? ` (${w.country}${w.city ? `, ${w.city}` : ''})` : ''}${w.live?.online === false ? ' — offline' : ''}`,
      disabled: w.live?.online === false,
    })),
  ];

  const canWrite = user?.role === 'admin' || user?.role === 'operator';

  const fetchDetail = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/health/servers/${key}`);
      setDetail(data.data);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load health detail');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [key, message, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleCheck = async () => {
    setChecking(true);
    try {
      await apiClient.post('/health/check', { serverKeys: [key] });
      message.success('Check triggered');
      setTimeout(fetchDetail, 3000);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to trigger check');
    } finally {
      setChecking(false);
    }
  };

  const openQuickCheck = (target: QuickCheckTarget) => {
    setQuickCheck(target);
    setQuickCheckOpen(true);
  };

  if (loading || !detail) return null;

  const isMobileGW = detail.type === 'mobile-gateway';
  const handlers = { token, canWrite, onQuickCheck: openQuickCheck, onHistory: setHistoryOf };
  const ipColumns = buildIpColumns(handlers);
  const proxyColumns = buildProxyColumns({ ...handlers, detail });
  const expandedRow = (record: IPHealth | ProxyHealth) => (
    <>
      <StatusTimelineBlock timeline={record.statusTimeline} state={record.state} />
      <StepsTable steps={record.steps} />
    </>
  );

  return (
    <>
      {/* Back, name, state and the check button on one line. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <Space size={8} align="center" style={{ minWidth: 0 }}>
          <Tooltip title="Back to Health Monitor">
            <Button size="small" aria-label="Back to Health Monitor" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} />
          </Tooltip>
          <Title level={4} style={{ margin: 0 }} ellipsis={{ tooltip: detail.name }}>{detail.name}</Title>
          <Tag color={STATUS_COLORS[detail.status] || 'default'} style={{ margin: 0 }}>
            {detail.status}
          </Tag>
        </Space>
        {canWrite && (
          <Button size="small" type="primary" onClick={handleCheck} loading={checking}>
            Run Check
          </Button>
        )}
      </div>

      <HealthIdentity detail={detail} />

      <Title level={5} style={{ margin: '0 0 6px' }}>Per-IP Health</Title>
      <Table
        dataSource={detail.ips}
        columns={ipColumns}
        rowKey="ip"
        size="small"
        tableLayout="fixed"
        pagination={false}
        expandable={{ columnWidth: 32, expandedRowRender: expandedRow }}
      />
      {detail.proxies && detail.proxies.length > 0 && (() => {
        const proxyID = (p: ProxyHealth) => `${p.host}:${p.port}`;
        const focusFound = focusedProxy ? detail.proxies.some((p) => proxyID(p) === focusedProxy) : false;
        // The endpoint from the link goes first instead of being highlighted:
        // the app has no stylesheet and hardcoding a colour would fight the
        // dark theme, while ordering reads the same everywhere.
        const ordered = focusFound
          ? [...detail.proxies].sort((a, b) =>
              (proxyID(b) === focusedProxy ? 1 : 0) - (proxyID(a) === focusedProxy ? 1 : 0))
          : detail.proxies;
        // One box over every field that identifies an endpoint, rather than a
        // filter per column: an operator types what they were handed — a port,
        // part of an address, a modem name, or a state.
        const needle = proxyFilter.trim().toLowerCase();
        const proxies = needle
          ? ordered.filter((p) => [p.protocol, p.host, String(p.port), proxyID(p), p.label, p.state, p.country]
              .some((v) => (v || '').toString().toLowerCase().includes(needle)))
          : ordered;
        return (
        <>
          <Space style={{ marginTop: 12, marginBottom: 6, width: '100%', justifyContent: 'space-between' }}>
            <Space size={6} align="baseline">
              <Title level={5} style={{ margin: 0 }}>
                {isMobileGW ? 'Modems' : 'Per-Proxy Health'}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {needle ? `${proxies.length} of ${detail.proxies.length}` : detail.proxies.length}
              </Text>
            </Space>
            <Input
              size="small"
              allowClear
              value={proxyFilter}
              onChange={(e) => setProxyFilter(e.target.value)}
              placeholder="Filter: host, port, protocol, modem, state…"
              prefix={<SearchOutlined />}
              style={{ width: 240 }}
            />
          </Space>
          {focusedProxy && (
            <Alert
              type={focusFound ? 'info' : 'warning'}
              showIcon
              banner
              style={{ marginBottom: 8, fontSize: 12 }}
              message={focusFound
                ? `Showing ${focusedProxy} first — its check steps are expanded below.`
                : `${focusedProxy} is not configured on this server. Listing every endpoint it does have.`}
            />
          )}
          <Table
            dataSource={proxies}
            columns={proxyColumns}
            rowKey={(record: ProxyHealth) => `${record.host}:${record.port}`}
            size="small"
            tableLayout="fixed"
            pagination={false}
            expandable={{
              columnWidth: 32,
              // The endpoint named in the URL opens with its steps already
              // unfolded — that detail is what the link was followed for.
              defaultExpandedRowKeys: focusedProxy ? [focusedProxy] : [],
              expandedRowRender: expandedRow,
            }}
          />
        </>
        );
      })()}

      <QuickCheckModal
        open={quickCheckOpen}
        serverKey={key || ''}
        target={quickCheck}
        workerOptions={qcWorkerOptions}
        onClose={() => setQuickCheckOpen(false)}
      />

      <CheckHistoryModal
        data={historyOf}
        onClose={() => setHistoryOf(null)}
        onOpenCheckLog={(target) => navigate(`/check-logs?serverKey=${key}&ip=${encodeURIComponent(target)}`)}
      />
    </>
  );
};

export default HealthDetailPage;
